import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatErr } from "../../../../../lib/api";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../../../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../../../../components/ui/dialog";
import { toast } from "sonner";
import { SectionCard, EmptyState } from "../shared";
import { ACCOUNTING_BASE } from "../accountingNav";
import { ReportFilterBar, ReportPrintShell, BalanceCheckBanner, useFinancialYears, downloadReportCsv, formatCurrency, formatDateDMY } from "./reportShared";

/**
 * Phase 8 - Vendor Payables Report (items 29/30). Per-Vendor Opening/Bills/
 * Payments/Adjustments/Closing Payable, from each Vendor's own dedicated
 * ledger (1:1 since Phase 4/5). The control check catches vendor-group
 * ledgers with posted activity that no active Vendor record currently
 * points at, rather than a tautological same-number comparison.
 *
 * Phase 15 (Vendor Bills & Payables) item 6 - "Vendor Statement": adds a
 * "View Statement" drill-down per vendor row, mirroring
 * MemberReceivablesReportPage's MemberStatementDialog, consuming the new
 * GET /society/accounting/reports/payables/statement endpoint. Opening/
 * Bills/Payments/Advances/Adjustments/Closing all fall directly out of that
 * one endpoint's ledger-movement rows (a vendor's own single ledger already
 * IS its complete statement), and each row drills down to its voucher via
 * VoucherViewPage - no new report logic was needed, only this UI.
 */
export default function VendorPayablesReportPage() {
  const navigate = useNavigate();
  const financialYears = useFinancialYears();
  const [financialYearId, setFinancialYearId] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [search, setSearch] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statementFor, setStatementFor] = useState(null);

  const params = { financialYearId, fromDate, toDate };

  const load = async () => {
    try {
      setLoading(true);
      const res = await api.get("/society/accounting/reports/payables", { params });
      setData(res.data);
    } catch (err) {
      toast.error(formatErr(err, "Could not load vendor payables report"));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [financialYearId, fromDate, toDate]);

  const rows = (data?.rows || []).filter((r) => !search || r.vendorName.toLowerCase().includes(search.toLowerCase()));
  const openLedger = (ledgerId) => navigate(`${ACCOUNTING_BASE}/reports/general-ledger?ledgerId=${ledgerId}&fromDate=${fromDate || data?.fromDate || ""}&toDate=${toDate || data?.toDate || ""}`);

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-extrabold text-[#111827]">Vendor Payables Report</h2>
        <p className="mt-1 text-sm text-[#6B7280]">Per-vendor outstanding payable, from each vendor's own posted accounting activity.</p>
      </div>

      <div className="report-screen-only space-y-5">
        <ReportFilterBar
          financialYears={financialYears} financialYearId={financialYearId} onFinancialYearChange={setFinancialYearId}
          fromDate={fromDate} toDate={toDate} onFromDateChange={setFromDate} onToDateChange={setToDate}
          search={search} onSearchChange={setSearch} searchPlaceholder="Vendor name"
          onExport={() => downloadReportCsv("/society/accounting/reports/payables", params, `vendor-payables-${data?.toDate || ""}.csv`)}
          onPrint={() => window.print()}
        />

        {loading ? (
          <div className="text-sm text-[#6B7280]">Loading...</div>
        ) : rows.length === 0 ? (
          <EmptyState>No vendor accounting history yet.</EmptyState>
        ) : (
          <>
            <SectionCard subtitle={`${data.financialYear ? `FY ${data.financialYear} — ` : ""}${data.fromDate} to ${data.toDate}`}>
              <div className="overflow-x-auto rounded-sm border border-[#E5E7EB]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Vendor</TableHead><TableHead className="text-right">Opening Payable</TableHead>
                      <TableHead className="text-right">Bills</TableHead><TableHead className="text-right">Paid</TableHead>
                      <TableHead className="text-right">Adjustments</TableHead><TableHead className="text-right">Advance</TableHead>
                      <TableHead className="text-right">Outstanding</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((r) => (
                      <TableRow key={r.vendorId}>
                        <TableCell><button type="button" className="text-left font-semibold text-[#111827] hover:underline" onClick={() => openLedger(r.ledgerId)}>{r.vendorName}</button></TableCell>
                        <TableCell className="text-right">{formatCurrency(r.openingPayable)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(r.bills)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(r.payments)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(r.adjustments)}</TableCell>
                        <TableCell className="text-right">{r.advance ? formatCurrency(r.advance) : "—"}</TableCell>
                        <TableCell className="text-right font-bold">{formatCurrency(r.outstanding)}</TableCell>
                        <TableCell><button type="button" className="text-xs font-semibold text-[#111827] hover:underline" onClick={() => setStatementFor(r)}>View Statement</button></TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="border-t-2 border-[#111827] font-bold">
                      <TableCell colSpan={6}>Vendor Detail Total</TableCell>
                      <TableCell className="text-right">{formatCurrency(data.detailTotal)}</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            </SectionCard>
            <BalanceCheckBanner label="Vendor Payables: Detail Total vs Vendor-Group Ledgers" difference={data.controlBalance - data.detailTotal} okLabel="Reconciled" />
            {data.orphanLedgers?.length > 0 && (
              <div className="rounded-sm border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
                {data.orphanLedgers.length} vendor-group ledger(s) have posted activity but no active Vendor record: {data.orphanLedgers.map((l) => l.ledgerName).join(", ")}.
              </div>
            )}
          </>
        )}
      </div>

      {rows.length > 0 && (
        <div className="report-print-only">
          <ReportPrintShell reportName="Vendor Payables Report" rangeLabel={`${data.financialYear ? `FY ${data.financialYear} — ` : ""}${data.fromDate} to ${data.toDate}`}>
            <table className="w-full border-collapse text-[10px]">
              <thead><tr className="border-b border-black"><th className="py-1 text-left">Vendor</th><th className="py-1 text-right">Opening</th><th className="py-1 text-right">Bills</th><th className="py-1 text-right">Paid</th><th className="py-1 text-right">Advance</th><th className="py-1 text-right">Outstanding</th></tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.vendorId} className="border-b border-gray-300">
                    <td className="py-0.5">{r.vendorName}</td>
                    <td className="py-0.5 text-right">{formatCurrency(r.openingPayable)}</td>
                    <td className="py-0.5 text-right">{formatCurrency(r.bills)}</td>
                    <td className="py-0.5 text-right">{formatCurrency(r.payments)}</td>
                    <td className="py-0.5 text-right">{r.advance ? formatCurrency(r.advance) : ""}</td>
                    <td className="py-0.5 text-right">{formatCurrency(r.outstanding)}</td>
                  </tr>
                ))}
                <tr className="border-t border-black font-bold"><td colSpan={5} className="py-1">Detail Total</td><td className="py-1 text-right">{formatCurrency(data.detailTotal)}</td></tr>
              </tbody>
            </table>
          </ReportPrintShell>
        </div>
      )}

      {statementFor && (
        <VendorStatementDialog
          vendor={statementFor} financialYearId={financialYearId} fromDate={fromDate} toDate={toDate}
          onClose={() => setStatementFor(null)}
          onOpenLedger={(ledgerId) => navigate(`${ACCOUNTING_BASE}/reports/general-ledger?ledgerId=${ledgerId}`)}
        />
      )}
    </div>
  );
}

function VendorStatementDialog({ vendor, financialYearId, fromDate, toDate, onClose, onOpenLedger }) {
  const navigate = useNavigate();
  const [statement, setStatement] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const res = await api.get("/society/accounting/reports/payables/statement", {
          params: { vendorId: vendor.vendorId, financialYearId, fromDate, toDate },
        });
        setStatement(res.data);
      } catch (err) {
        toast.error(formatErr(err, "Could not load vendor statement"));
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendor]);

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader><DialogTitle>Statement — {vendor.vendorName}</DialogTitle></DialogHeader>
        {loading ? (
          <div className="text-sm text-[#6B7280]">Loading...</div>
        ) : !statement ? (
          <div className="text-sm text-[#6B7280]">No data.</div>
        ) : (
          <div className="max-h-[70vh] space-y-4 overflow-y-auto">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div><span className="text-[#6B7280]">Opening Payable:</span> <span className="font-semibold">{formatCurrency(statement.openingPayable)}</span></div>
              <div><span className="text-[#6B7280]">Closing Payable:</span> <span className="font-semibold">{formatCurrency(statement.closingPayable)}</span></div>
              <div><span className="text-[#6B7280]">Opening Advance:</span> <span className="font-semibold">{formatCurrency(statement.openingAdvance)}</span></div>
              <div><span className="text-[#6B7280]">Closing Advance:</span> <span className="font-semibold">{formatCurrency(statement.closingAdvance)}</span></div>
            </div>
            <div>
              <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-[#6B7280]">Bills, Payments, Advances &amp; Adjustments</div>
              <div className="overflow-x-auto rounded-sm border border-[#E5E7EB]">
                <Table>
                  <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Voucher</TableHead><TableHead>Particulars</TableHead><TableHead className="text-right">Debit</TableHead><TableHead className="text-right">Credit</TableHead><TableHead className="text-right">Balance</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {statement.rows.map((row, i) => (
                      <TableRow key={i}>
                        <TableCell>{formatDateDMY(row.voucherDate)}</TableCell>
                        <TableCell>{row.voucherId ? <button type="button" className="font-semibold text-[#111827] hover:underline" onClick={() => navigate(`${ACCOUNTING_BASE}/vouchers/${row.voucherId}`)}>{row.voucherNumber}</button> : "-"}</TableCell>
                        <TableCell>{row.narration}</TableCell>
                        <TableCell className="text-right">{row.debit ? formatCurrency(row.debit) : "—"}</TableCell>
                        <TableCell className="text-right">{row.credit ? formatCurrency(row.credit) : "—"}</TableCell>
                        <TableCell className="text-right">{formatCurrency(row.runningBalance)}</TableCell>
                      </TableRow>
                    ))}
                    {statement.rows.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-sm text-[#6B7280]">No transactions in this range.</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </div>
              {statement.ledgerId && <button type="button" className="mt-1 text-xs font-semibold text-[#111827] hover:underline" onClick={() => onOpenLedger(statement.ledgerId)}>Open full Vendor ledger &rarr;</button>}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
